import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { verseService } from '../../../services/supabaseService';

/**
 * Coverage tiers based on % of verses reviewed within 30 days.
 * Never-reviewed verses count as 90 days (overdue).
 *
 * Green      : 100%
 * Light Green : >= 90%
 * Yellow     : >= 80%
 * Orange     : >= 50%
 * Red        : < 50%
 */
const COVERAGE_TIERS = [
  { label: 'Excellent', minPct: 100, color: '#22c55e', bg: 'from-green-500/10 to-green-600/5',  border: 'border-green-500/30',  text: 'text-green-500',  ring: '#22c55e' },
  { label: 'Great',     minPct: 90,  color: '#84cc16', bg: 'from-lime-500/10 to-lime-600/5',    border: 'border-lime-500/30',   text: 'text-lime-500',   ring: '#84cc16' },
  { label: 'Good',      minPct: 80,  color: '#eab308', bg: 'from-yellow-500/10 to-yellow-600/5',border: 'border-yellow-500/30', text: 'text-yellow-500', ring: '#eab308' },
  { label: 'Fair',      minPct: 50,  color: '#f97316', bg: 'from-orange-500/10 to-orange-600/5',border: 'border-orange-500/30', text: 'text-orange-500', ring: '#f97316' },
  { label: 'Critical',  minPct: 0,   color: '#ef4444', bg: 'from-red-500/10 to-red-600/5',      border: 'border-red-500/30',   text: 'text-red-500',   ring: '#ef4444' },
];

/**
 * Legacy avg-days tiers (kept for secondary ring).
 */
const HEALTH_TIERS = [
  { label: 'Healthy',  maxDays: 30,  color: '#22c55e', bg: 'from-green-500/10 to-green-600/5',  border: 'border-green-500/30',  text: 'text-green-500',  ring: '#22c55e' },
  { label: 'Good',     maxDays: 45,  color: '#84cc16', bg: 'from-lime-500/10 to-lime-600/5',    border: 'border-lime-500/30',   text: 'text-lime-500',   ring: '#84cc16' },
  { label: 'Fair',     maxDays: 60,  color: '#eab308', bg: 'from-yellow-500/10 to-yellow-600/5',border: 'border-yellow-500/30', text: 'text-yellow-500', ring: '#eab308' },
  { label: 'Fading',   maxDays: 90,  color: '#f97316', bg: 'from-orange-500/10 to-orange-600/5',border: 'border-orange-500/30', text: 'text-orange-500', ring: '#f97316' },
  { label: 'Critical', maxDays: Infinity, color: '#ef4444', bg: 'from-red-500/10 to-red-600/5', border: 'border-red-500/30',   text: 'text-red-500',   ring: '#ef4444' },
];

const NEVER_REVIEWED_DEFAULT_DAYS = 90;
const TARGET_DAYS = 30;

export function getTier(avgDays) {
  return HEALTH_TIERS?.find(t => avgDays <= t?.maxDays) || HEALTH_TIERS?.[HEALTH_TIERS?.length - 1];
}

export function getCoverageTier(pct) {
  if (pct >= 100) return COVERAGE_TIERS?.[0];
  if (pct >= 90)  return COVERAGE_TIERS?.[1];
  if (pct >= 80)  return COVERAGE_TIERS?.[2];
  if (pct >= 50)  return COVERAGE_TIERS?.[3];
  return COVERAGE_TIERS?.[4];
}

/** SVG donut ring — animates from 0 to pct on mount */
function CoverageRing({ pct, color, size = 88, animate = false, label, sublabel }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const [displayPct, setDisplayPct] = useState(0);

  useEffect(() => {
    if (!animate) {
      setDisplayPct(pct);
      return;
    }
    let start = null;
    const duration = 1100;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayPct(pct * eased);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [pct, animate]);

  const dash = (displayPct / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 drop-shadow-sm">
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="currentColor" strokeWidth="9"
          className="text-muted/40"
        />
        {/* Progress */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth="9"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.04s linear' }}
        />
        {/* Center text */}
        <text
          x={size / 2} y={size / 2 + 1}
          textAnchor="middle" dominantBaseline="middle"
          fontSize="14" fontWeight="800" fill={color}
        >
          {Math.round(displayPct)}%
        </text>
      </svg>
      {label && (
        <span className="text-[10px] text-muted-foreground leading-tight text-center whitespace-pre-line">
          {label}
        </span>
      )}
      {sublabel && (
        <span className="text-[9px] font-semibold leading-tight text-center" style={{ color }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

/** Small inline health ring for use in Header */
export function HealthMiniRing({ avgDays, size = 20 }) {
  const tier = getTier(avgDays ?? 999);
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const healthPct = Math.max(0, Math.min(100, 100 - (avgDays / (TARGET_DAYS * 2)) * 100));
  const dash = (healthPct / 100) * circ;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={tier?.color + '33'} strokeWidth="3" />
      <circle
        cx={size/2} cy={size/2} r={r}
        fill="none" stroke={tier?.color} strokeWidth="3"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
    </svg>
  );
}

/** Animated horizontal health tier bar */
function AnimatedHealthBar({ tiers, activeTier, animate }) {
  const [barWidths, setBarWidths] = useState(tiers?.map(() => 0));

  useEffect(() => {
    if (!animate) {
      setBarWidths(tiers?.map(() => 100));
      return;
    }
    tiers?.forEach((_, i) => {
      setTimeout(() => {
        setBarWidths(prev => {
          const next = [...prev];
          next[i] = 100;
          return next;
        });
      }, 200 + i * 100);
    });
  }, [animate]);

  return (
    <div className="flex gap-0.5 rounded-full overflow-hidden h-2">
      {tiers?.map((t, i) => (
        <div
          key={t?.label}
          className="flex-1 rounded-full overflow-hidden"
          style={{ backgroundColor: t?.color + '33' }}
        >
          <div
            className="h-full rounded-full"
            style={{
              backgroundColor: t?.color,
              opacity: activeTier?.label === t?.label ? 1 : 0.25,
              width: `${barWidths?.[i]}%`,
              transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function MemoryHealthCard({ onExtraReview, verses: propVerses }) {
  const { user } = useAuth();
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (propVerses !== undefined) {
      setVerses(propVerses || []);
      setLoading(false);
      return;
    }

    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await verseService?.getUserVerses(user?.id);
        setVerses(data || []);
      } catch (e) {
        console.error('MemoryHealthCard load error', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id, propVerses]);

  // Entrance animation via IntersectionObserver
  useEffect(() => {
    if (loading) return;
    const el = cardRef?.current;
    if (!el) { setVisible(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [loading]);

  const stats = useMemo(() => {
    const allVerses = verses || [];

    const activeVerses = allVerses?.filter(v => {
      const phase = v?.currentPhase ?? v?.current_phase ?? 0;
      return phase >= 1 && phase <= 7;
    });

    const total = activeVerses?.length;
    if (total === 0) return null;

    const now = Date.now();
    const MS_PER_DAY = 86400000;

    const daysList = activeVerses?.map(v => {
      const lastReview = v?.lastReviewedAt || v?.last_reviewed_at;
      if (!lastReview) return NEVER_REVIEWED_DEFAULT_DAYS;
      const days = Math.floor((now - new Date(lastReview)?.getTime()) / MS_PER_DAY);
      return Math.max(0, days);
    });

    const neverReviewedCount = activeVerses?.filter(v => {
      const lastReview = v?.lastReviewedAt || v?.last_reviewed_at;
      return !lastReview;
    })?.length;

    const avgDays = daysList?.reduce((s, d) => s + d, 0) / total;
    const reviewedThisMonth = daysList?.filter(d => d <= 30)?.length;
    // Primary metric: % reviewed within 30 days
    const coveragePct = (reviewedThisMonth / total) * 100;
    // Secondary metric: avg days ring (same as before)
    const avgDaysPct = Math.max(0, Math.min(100, 100 - (avgDays / (TARGET_DAYS * 2)) * 100));
    const fadingCount = daysList?.filter(d => d > 30 && d <= 60)?.length;
    const criticalCount = daysList?.filter(d => d > 60)?.length;
    const overdueCount = daysList?.filter(d => d > 30)?.length;

    return { total, avgDays, avgDaysPct, reviewedThisMonth, coveragePct, fadingCount, criticalCount, neverReviewedCount, overdueCount };
  }, [verses]);

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-4 shadow-md animate-pulse">
        <div className="h-5 bg-muted rounded w-1/2 mb-3" />
        <div className="h-24 bg-muted rounded" />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Primary tier is now based on coverage %
  const coverageTier = getCoverageTier(stats?.coveragePct);
  // Secondary tier based on avg days (kept for secondary ring)
  const avgTier = getTier(stats?.avgDays);

  const isOnTarget = stats?.avgDays <= TARGET_DAYS;
  const daysToTarget = Math.max(0, Math.round(stats?.avgDays - TARGET_DAYS));

  return (
    <div
      ref={cardRef}
      className={`bg-gradient-to-br ${coverageTier?.bg} rounded-xl border ${coverageTier?.border} p-4 sm:p-5 shadow-md transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          {/* Animated pulsing dot */}
          <div className="relative flex items-center justify-center w-3.5 h-3.5 flex-shrink-0">
            <div
              className="absolute w-3.5 h-3.5 rounded-full animate-ping opacity-40"
              style={{ backgroundColor: coverageTier?.color }}
            />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: coverageTier?.color }} />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-foreground tracking-tight">Library Memory Health</h3>
        </div>
        {/* Assessment badge — driven by primary coverage tier */}
        <span
          className={`text-sm font-extrabold px-3 py-1 rounded-full border ${coverageTier?.border} ${coverageTier?.text}`}
          style={{ backgroundColor: coverageTier?.color + '20' }}
        >
          {coverageTier?.label}
        </span>
      </div>

      {/* Two rings side by side */}
      <div className="flex items-start gap-3 sm:gap-4 mb-3">

        {/* PRIMARY ring: 30-day coverage % */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <div className="relative">
            {/* "PRIMARY" badge */}
            <span
              className="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] font-bold px-1.5 py-0.5 rounded-full z-10 whitespace-nowrap"
              style={{ backgroundColor: coverageTier?.color + '30', color: coverageTier?.color, border: `1px solid ${coverageTier?.color}50` }}
            >
              PRIMARY
            </span>
            <CoverageRing
              pct={stats?.coveragePct}
              color={coverageTier?.color}
              size={96}
              animate={visible}
            />
          </div>
          <span className="text-[10px] text-muted-foreground leading-tight text-center">
            reviewed in
          </span>
          <span className="text-[10px] text-muted-foreground leading-tight text-center">
            30 days
          </span>
          <span className="text-[9px] font-semibold leading-tight text-center" style={{ color: coverageTier?.color }}>
            {stats?.reviewedThisMonth}/{stats?.total} verses
          </span>
        </div>

        {/* SECONDARY ring: avg days coverage */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <CoverageRing
            pct={stats?.avgDaysPct}
            color={avgTier?.color}
            size={80}
            animate={visible}
          />
          <span className="text-[10px] text-muted-foreground leading-tight text-center">
            avg days
          </span>
          <span className="text-[10px] text-muted-foreground leading-tight text-center">
            coverage
          </span>
          <span className="text-[9px] font-semibold leading-tight text-center" style={{ color: isOnTarget ? '#22c55e' : avgTier?.color }}>
            {isOnTarget ? `✓ on target` : `${Math.round(stats?.avgDays)}d avg`}
          </span>
        </div>

        {/* Stats column */}
        <div className="flex-1 space-y-2 min-w-0">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{stats?.reviewedThisMonth}</span> of{' '}
            <span className="font-semibold text-foreground">{stats?.total}</span> verses reviewed this month
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-background/50 rounded-lg p-2 text-center">
              <div
                className="text-xl font-bold"
                style={{ color: isOnTarget ? '#22c55e' : avgTier?.color }}
              >
                {Math.round(stats?.avgDays)}
              </div>
              <div className="text-[10px] text-muted-foreground">avg days since review</div>
              <div className="mt-1.5 flex items-center gap-1">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: visible ? `${Math.min(100, (stats?.avgDays / (TARGET_DAYS * 2)) * 100)}%` : '0%',
                      backgroundColor: isOnTarget ? '#22c55e' : avgTier?.color,
                      transition: 'width 1s cubic-bezier(0.4,0,0.2,1) 0.3s',
                    }}
                  />
                </div>
              </div>
              <div className="text-[9px] mt-0.5" style={{ color: isOnTarget ? '#22c55e' : avgTier?.color }}>
                {isOnTarget ? `✓ on target` : `${daysToTarget}d over target`}
              </div>
            </div>
            <div className="bg-background/50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold" style={{ color: stats?.fadingCount > 0 ? '#f97316' : '#22c55e' }}>
                {stats?.fadingCount}
              </div>
              <div className="text-[10px] text-muted-foreground">fading verses</div>
            </div>
          </div>

          {/* Coverage tier scale */}
          <div className="flex gap-0.5 rounded-full overflow-hidden h-2">
            {COVERAGE_TIERS?.slice()?.reverse()?.map((t) => (
              <div
                key={t?.label}
                className="flex-1 rounded-full"
                style={{
                  backgroundColor: t?.color,
                  opacity: coverageTier?.label === t?.label ? 1 : 0.25,
                }}
              />
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-muted-foreground px-0.5">
            <span>Critical</span>
            <span className="text-[9px]">goal: 100% in 30d</span>
            <span>Excellent</span>
          </div>
        </div>
      </div>

      {/* Never reviewed notice */}
      {stats?.neverReviewedCount > 0 && (
        <div className="mt-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{stats?.neverReviewedCount}</span> verse{stats?.neverReviewedCount !== 1 ? 's' : ''} never reviewed (counted as {NEVER_REVIEWED_DEFAULT_DAYS} days)
        </div>
      )}

      {/* Nudge + CTA */}
      {(stats?.fadingCount > 0 || stats?.criticalCount > 0) && (
        <div className="mt-3 pt-3 border-t border-border/40">
          <p className="text-xs text-muted-foreground mb-2">
            {stats?.fadingCount > 0
              ? `Do an Extra Review to cover ${stats?.fadingCount} fading verse${stats?.fadingCount !== 1 ? 's' : ''}`
              : `${stats?.criticalCount} verse${stats?.criticalCount !== 1 ? 's' : ''} haven't been reviewed in 60+ days`}
          </p>
          <button
            onClick={() => onExtraReview?.()}
            className="w-full py-3 px-3 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5 transition-all duration-200 bg-gradient-to-r from-accent to-primary text-primary-foreground hover:from-accent/90 hover:to-primary/90 shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] border border-accent/20 min-h-[44px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Extra Review
          </button>
        </div>
      )}
    </div>
  );
}
